import { motion } from "framer-motion";
import { TrendingUp, Sparkles, Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";

interface HeroSectionProps {
  onGetStarted: () => void;
  totalRuns?: number | null;
}

const HeroSection = ({ onGetStarted, totalRuns }: HeroSectionProps) => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/20 bg-gold/5 mb-8">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-sm text-gold font-display">{t("hero.badge")}</span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display text-3xl sm:text-5xl md:text-7xl font-bold tracking-tight mb-6"
        >
          {t("hero.title1")}{" "}
          <span className="text-gradient-gold">{t("hero.title2")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed"
        >
          {t("hero.subtitle")}
        </motion.p>

        {totalRuns !== null && totalRuns !== undefined && totalRuns > 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/5 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
            <span className="text-xs sm:text-sm text-emerald-300 font-display">
              <span className="font-bold">{totalRuns.toLocaleString()}</span> AI Buy signals generated
            </span>
          </motion.div>
        )}


        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button variant="gold" size="xl" onClick={onGetStarted}>
            {t("hero.cta")}
            <ArrowRight className="w-5 h-5" />
          </Button>
          <Button variant="gold-outline" size="xl" onClick={() => {
            const el = document.getElementById("pricing");
            el?.scrollIntoView({ behavior: "smooth" });
          }}>
            {t("hero.viewPricing")}
          </Button>
        </motion.div>

        {/* How it works mini */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-10 max-w-sm mx-auto"
        >
          <div className="rounded-2xl border border-gold/20 bg-card/60 backdrop-blur-sm p-4 text-left">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-display mb-2">{t("hero.howItWorksLabel")}</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center text-[10px] font-bold text-gold">1</span>
                <span className="text-sm font-display">{t("hero.step1")}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center text-[10px] font-bold text-gold">2</span>
                <span className="text-sm font-display">{t("hero.step2")}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center text-[10px] font-bold text-gold">3</span>
                <span className="text-sm font-display">{t("hero.step3")}</span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-4 text-[11px] text-muted-foreground/50 font-display tracking-wide"
        >
          {t("hero.powered")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-8 grid grid-cols-3 gap-8 max-w-lg mx-auto"
        >
          {[
            { icon: TrendingUp, label: t("hero.smartPicks") },
            { icon: Shield, label: t("hero.riskAnalysis") },
            { icon: Sparkles, label: t("hero.roiForecast") },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center border border-border">
                <Icon className="w-5 h-5 text-gold" />
              </div>
              <span className="text-xs text-muted-foreground font-display">{label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
